import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RangeSwitchComponent } from '../../components/shared/range-switch.component';
import { InfoHintComponent } from '../../components/shared/info-hint.component';
import { PersonLinkComponent } from '../../components/shared/person-link.component';
import { MatDialog } from '@angular/material/dialog';
import {
  PeerChatsDialogComponent,
  PeerChatsDialogData,
} from './peer-chats-dialog/peer-chats-dialog.component';
import { EventLinkComponent } from '../../components/shared/event-link.component';
import { UsersService } from '../../services/users-service.service';
import {
  EventInsightRowDto,
  EventsInsightOverviewDto,
  LobbyState,
} from '../../data-transfer-objects/events-insight.dto';

/** What each lobby state means and what it asks of the organizer. */
export const LOBBY_STATE_LABEL: Record<LobbyState, string> = {
  silent: 'Silent',
  quiet: 'Quiet',
  'one-voice': 'One voice',
  'some-replies': 'Some replies',
  conversation: 'Conversation',
};

export const LOBBY_STATE_HINT: Record<LobbyState, string> = {
  silent: 'Nobody has ever posted in this lobby.',
  quiet: 'Had messages before, none in the chosen range.',
  'one-voice': 'Only one person posted in range — a noticeboard, not a room.',
  'some-replies': 'Two or more people posted, but the speaker changed fewer than 4 times.',
  conversation: 'The speaker changed 4 or more times in range — people are actually talking.',
};

type SortKey = 'active' | 'members' | 'newest' | 'waiting';

/**
 * The events board.
 *
 * One row per space: who runs it, who is in it, and whether its lobby is a
 * conversation or a noticeboard. The lobby columns are the point - a space
 * with 80 members and one voice is a mailing list, and the member count on
 * its own would call it a success.
 *
 * Nothing said in any lobby is fetched. Counts, voices and timestamps only.
 */
@Component({
  selector: 'app-events',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    RangeSwitchComponent,
    InfoHintComponent,
    PersonLinkComponent,
    EventLinkComponent,
  ],
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
})
export class EventsComponent implements OnInit {
  overview: EventsInsightOverviewDto | null = null;
  loading = true;
  error: string | null = null;
  days = 28;
  filter: LobbyState | 'all' = 'all';
  sort: SortKey = 'newest';

  /*
   * Unclaimed spaces are out of the table by default.
   *
   * Most of the 170 rows are campaign spaces nobody has taken yet: no
   * organizer, no members, no lobby, and nothing about them changes until an
   * invitation is answered. They filled the board and pushed the spaces that
   * have people in them off the first screen. The checkbox brings them back
   * for the outreach questions they do answer - which invitations are still
   * unanswered, and how old they are.
   */
  showUnclaimed = false;

  readonly stateLabel = LOBBY_STATE_LABEL;
  readonly stateHint = LOBBY_STATE_HINT;
  readonly states: LobbyState[] = ['conversation', 'some-replies', 'one-voice', 'quiet', 'silent'];

  constructor(private usersService: UsersService, private dialog: MatDialog) {}

  /**
   * Who chatted with whom in one space.
   *
   * The outcome column was a number with nowhere to go: '5 chats, 2 replied'
   * does not say whether five people each found somebody or one member wrote
   * to five strangers and three ignored them. A dialog rather than a route,
   * like the profile links beside it - the board is filtered and sorted, and
   * navigating away to answer one question throws that away.
   */
  openPeerChats(e: EventInsightRowDto, event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open<PeerChatsDialogComponent, PeerChatsDialogData>(PeerChatsDialogComponent, {
      width: '760px',
      maxWidth: '96vw',
      autoFocus: false,
      data: { eventId: e.id, title: e.title, detail: null },
    });
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.usersService.getEventsInsight(this.days).subscribe({
      next: (o) => {
        this.overview = o;
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load the events board: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  setRange(days: number): void {
    this.days = days;
    this.load();
  }

  /** The spaces the board is showing at all, before the lobby-state chips. */
  get visible(): EventInsightRowDto[] {
    const all = this.overview?.events ?? [];
    return this.showUnclaimed ? all : all.filter((e) => e.owner);
  }

  /** Spaces the checkbox is currently keeping out of the table. */
  get unclaimedCount(): number {
    return (this.overview?.events ?? []).filter((e) => !e.owner).length;
  }

  get rows(): EventInsightRowDto[] {
    const rows = this.visible.filter(
      (e) => this.filter === 'all' || e.lobby.state === this.filter
    );
    const by: Record<SortKey, (a: EventInsightRowDto, b: EventInsightRowDto) => number> = {
      active: (a, b) =>
        b.lobby.window.messages - a.lobby.window.messages ||
        b.lobby.allTime.messages - a.lobby.allTime.messages ||
        b.memberCount - a.memberCount,
      members: (a, b) => b.memberCount - a.memberCount || b.joinedSince - a.joinedSince,
      newest: (a, b) => b.createdAt.localeCompare(a.createdAt),
      waiting: (a, b) => b.pendingRequests - a.pendingRequests || b.memberCount - a.memberCount,
    };
    return [...rows].sort(by[this.sort]);
  }

  /* Counted over what the board is showing, not over everything: a chip
     reading 134 next to a table of 36 rows is a chip nobody can trust. */
  countIn(state: LobbyState): number {
    return this.visible.filter((e) => e.lobby.state === state).length;
  }

  /** Share of messages that came from the owner or a moderator, in range, else ever. */
  organizerShare(e: EventInsightRowDto): number | null {
    const s = e.lobby.window.messages ? e.lobby.window : e.lobby.allTime;
    return s.messages ? Math.round((s.organizerMessages / s.messages) * 100) : null;
  }

  percent(rate: number | null): string {
    return rate === null ? '—' : `${Math.round(rate * 100)}%`;
  }

  /** "3d ago" style, for the last-activity column. */
  ago(iso: string | null): string {
    if (!iso) return '—';
    const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  /** When the event runs, in one short line. */
  when(e: EventInsightRowDto): string {
    if (!e.startDate) return e.type === 'Community' ? 'ongoing' : 'no dates';
    const fmt = (iso: string) =>
      new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const start = fmt(e.startDate);
    if (!e.endDate || e.endDate.slice(0, 10) === e.startDate.slice(0, 10)) return start;
    return `${start} – ${fmt(e.endDate)}`;
  }

  /** Runs today, is over, or has not started yet. */
  timing(e: EventInsightRowDto): 'upcoming' | 'live' | 'past' | null {
    if (!e.startDate) return null;
    const now = Date.now();
    if (Date.parse(e.startDate) > now) return 'upcoming';
    const end = e.endDate ? Date.parse(e.endDate) + 86_400_000 : Date.parse(e.startDate) + 86_400_000;
    return end < now ? 'past' : 'live';
  }
}
